import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { contactInfo } from '../data/mock';
import { Clock, MapPin, Zap, Send, ExternalLink } from 'lucide-react';

function PhilosophySection() {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="about" className="bg-[#050505] py-20 md:py-32 border-t border-white/5">
      <div
        ref={ref}
        className={`max-w-[1440px] mx-auto px-6 md:px-10 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-1/3">
            <span className="font-mono text-[10px] tracking-[0.3em] text-emerald-500/50 uppercase">
              Vision
            </span>
            <h3 className="text-sm font-mono text-white/40 mt-2">Our Philosophy</h3>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mt-4 tracking-[-0.02em] leading-tight text-white">
              The Science of
              <br />
              <span className="text-emerald-400/40">Player</span>
              <br />
              <span className="text-emerald-400/40">Acquisition.</span>
            </h2>

            <div className="flex flex-wrap gap-4 mt-8">
              <div className="flex items-center gap-2">
                <MapPin size={12} className="text-emerald-500/40" />
                <div>
                  <span className="font-mono text-[9px] tracking-[0.15em] text-white/25">HQ</span>
                  <span className="font-mono text-xs text-white/60 ml-2">Malta</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Zap size={12} className="text-emerald-500/40" />
                <div>
                  <span className="font-mono text-[9px] tracking-[0.15em] text-white/25">Focus</span>
                  <span className="font-mono text-xs text-white/60 ml-2">iGaming Affiliates</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-2/3">
            <div className="space-y-6">
              <p className="font-mono text-sm leading-relaxed text-white/40">
                Every player journey is a story we optimize. In a crowded iGaming landscape, we build the brands, content, and technology that connect the right player with the right operator — at the right moment.
              </p>
              <p className="font-mono text-sm leading-relaxed text-white/40">
                Data is our compass. Performance is our currency. We don't guess — we measure, test, and scale what works. Our affiliate brands generate millions of monthly visits through organic search, intelligent content, and relentless optimization.
              </p>
              <p className="font-mono text-sm leading-relaxed text-white/40">
                We partner with the world's leading casino and sportsbook operators, delivering high-value players through trusted, compliance-first platforms built for long-term growth.
              </p>
            </div>

            {/* Stats */}
            <div className="flex gap-8 md:gap-16 mt-12 pt-8 border-t border-white/5">
              <div>
                <div className="text-3xl md:text-4xl font-black text-emerald-400">12+</div>
                <div className="font-mono text-[10px] tracking-[0.15em] text-white/30 mt-1">Active Brands</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-black text-emerald-400">50+</div>
                <div className="font-mono text-[10px] tracking-[0.15em] text-white/30 mt-1">Operator Partners</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-black text-emerald-400">5M+</div>
                <div className="font-mono text-[10px] tracking-[0.15em] text-white/30 mt-1">Monthly Visits</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

function ContactSection() {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="contact" className="bg-[#050505] py-20 md:py-32 border-t border-white/5">
      <div
        ref={ref}
        className={`max-w-[1440px] mx-auto px-6 md:px-10 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left: Header */}
          <div className="lg:w-1/2">
            <span className="font-mono text-[10px] tracking-[0.3em] text-emerald-500/50 uppercase">
              Contact
            </span>
            <h3 className="text-sm font-mono text-white/40 mt-2">Inquiries Open</h3>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black mt-4 tracking-[-0.02em] leading-tight text-white">
              Ready To
              <br />
              <span className="text-white/20">Innovate?</span>
            </h2>
            <p className="font-mono text-xs text-white/35 mt-6 max-w-md leading-relaxed">
              Whether you're an operator looking for premium traffic or a brand seeking a strategic affiliate partner — let's talk.
            </p>
          </div>

          {/* Right: Info Cards */}
          <div className="lg:w-1/2 space-y-6">
            {/* Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-[#0d0d0d] border border-white/5 rounded-xl p-5 hover:border-emerald-500/15 transition-colors">
                <Clock size={16} className="text-emerald-500/40 mb-3" />
                <div className="font-mono text-[9px] tracking-[0.2em] text-white/25 uppercase">Availability</div>
                <div className="font-bold text-lg mt-1 text-white">{contactInfo.availability}</div>
                <div className="font-mono text-[10px] text-white/30 mt-1">in {contactInfo.availabilityMonth}</div>
              </div>
              <div className="bg-[#0d0d0d] border border-white/5 rounded-xl p-5 hover:border-emerald-500/15 transition-colors">
                <MapPin size={16} className="text-emerald-500/40 mb-3" />
                <div className="font-mono text-[9px] tracking-[0.2em] text-white/25 uppercase">Location</div>
                <div className="font-bold text-lg mt-1 text-white">{contactInfo.location}</div>
                <div className="font-mono text-[10px] text-white/30 mt-1">{contactInfo.timezone}</div>
              </div>
              <div className="bg-[#0d0d0d] border border-white/5 rounded-xl p-5 hover:border-emerald-500/15 transition-colors">
                <Zap size={16} className="text-emerald-500/40 mb-3" />
                <div className="font-mono text-[9px] tracking-[0.2em] text-white/25 uppercase">Avg. Response</div>
                <div className="font-bold text-lg mt-1 text-white">{contactInfo.avgResponse}</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <div className="bg-[#0d0d0d] border border-white/5 rounded-xl p-5 hover:border-emerald-500/15 transition-colors">
                <div className="font-mono text-[10px] tracking-[0.2em] text-white/25 uppercase mb-3">
                  Start A Project
                </div>
                <a
                  href="mailto:partnerships@go2media.io?subject=Partnership%20Inquiry"
                  className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] px-6 py-3 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all duration-300">

                  <Send size={14} />
                  Send Message
                </a>
                <div className="font-mono text-[10px] text-white/25 mt-3">
                  Email Directly: <a href="mailto:partnerships@go2media.io" className="text-emerald-400/60 hover:text-emerald-400 underline transition-colors">{contactInfo.email}</a>
                </div>
              </div>

              <div className="bg-[#0d0d0d] border border-white/5 rounded-xl p-5 hover:border-emerald-500/15 transition-colors">
                <div className="font-mono text-[10px] tracking-[0.2em] text-white/25 uppercase mb-3">
                  Operator Looking for Traffic?
                </div>
                <a
                  href="mailto:partnerships@go2media.io?subject=Operator%20Partnership"
                  className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] px-6 py-3 border border-emerald-500/30 rounded-full text-emerald-400 hover:bg-emerald-500 hover:text-black transition-all duration-300">

                  <ExternalLink size={14} />
                  Start Partnership
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

function Footer() {
  return (
    <footer className="bg-[#050505] border-t border-white/5 py-12">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full border-2 border-emerald-500/50 flex items-center justify-center relative overflow-hidden">
              <span className="text-[7px] font-black text-emerald-400">G2</span>
            </div>
            <span className="font-mono text-xs font-bold tracking-[0.15em] uppercase text-white/70">
              GO2 MEDIA
            </span>
          </div>

          {/* Links */}
          <div className="flex items-center gap-6">
            {['Work', 'Capabilities', 'Team', 'About', 'Contact'].map((link) =>
            <button
              key={link}
              onClick={() => {
                const el = document.querySelector(`#${link.toLowerCase()}`);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="font-mono text-[10px] tracking-[0.15em] text-white/20 hover:text-emerald-400 transition-colors uppercase">

                {link}
              </button>
            )}
          </div>

          {/* Copyright */}
          <div className="font-mono text-[10px] tracking-[0.1em] text-white/20">
            &copy; {new Date().getFullYear()} Go2 Media. All rights reserved.
          </div>
        </div>
      </div>
    </footer>);

}

export default function LowerSections() {
  return (
    <>
      <PhilosophySection />
      <ContactSection />
      <Footer />
    </>);

}